import {
    useCallback,
    useSyncExternalStore,
} from 'react';

export const UTC_TIMEZONE_ID = 'UTC';

// Deliberately not cleared on logout (ClearStorage.ts removes keys selectively).
const STORAGE_KEY = 'PORTAL_CONSUMPTION_TIMEZONE';

export const isValidTimezoneId = (value: unknown): value is string => {
    if (typeof value !== 'string') {
        return false;
    }
    try {
        new Intl.DateTimeFormat('en-US', { timeZone: value });
        return true;
    } catch {
        return false;
    }
};

export const getBrowserTimezoneId = (): string | null => {
    try {
        const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        return isValidTimezoneId(zone) ? zone : null;
    } catch {
        return null;
    }
};

const readStoredTimezoneId = (): string => {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        return isValidTimezoneId(stored) ? stored : UTC_TIMEZONE_ID;
    } catch {
        return UTC_TIMEZONE_ID;
    }
};

let currentTimezoneId = readStoredTimezoneId();
const listeners = new Set<() => void>();

const subscribe = (listener: () => void): (() => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
};

const getSnapshot = (): string => currentTimezoneId;

export const setConsumptionTimezoneId = (timeZoneId: string): void => {
    const next = isValidTimezoneId(timeZoneId) ? timeZoneId : UTC_TIMEZONE_ID;
    if (next === currentTimezoneId) {
        return;
    }
    currentTimezoneId = next;
    try {
        localStorage.setItem(STORAGE_KEY, next);
    } catch {
        // storage unavailable
    }
    listeners.forEach(listener => listener());
};

export function useConsumptionTimezone() {
    const timezoneId = useSyncExternalStore(subscribe, getSnapshot);
    const setTimezoneId = useCallback((next: string) => setConsumptionTimezoneId(next), []);

    return {
        timezoneId,
        setTimezoneId,
    };
}
